// Убираем тернарник из roleSlug везде где он используется
// Было: role === USER_ROLES.SV ? USER_ROLES.TA : USER_ROLES.MD
// Стало: MANAGER_AGENT_VOCABULARY[role] ?? role

import { Empty, Flex, Form, notification } from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useState } from "react";
import { Location, useLocation, useNavigate } from "react-router-dom";
import {
    getApiError,
    getMarketInfoCollection,
    getPlanConfigurationInfo,
    getSchedulerMyRoutesThunk,
    getUserMeThunk,
    getUserTeamGetByManagerThunk,
    patchRouteUpdate,
    patchSchedulerSetSchedulePoints,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Market, RouteDetails, Schedule } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { ROUTES } from "@/constants";
import { isManagerRole } from "@/hooks/isManagerRole";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { getSchedulerAllScheduleSvThunk } from "@/store/slices/sv";

import FiltersPanel from "./components/FiltersPanel";
import { ModalsContainer } from "./components/ModalsContainer";
import { MultiSelectorHeader } from "./components/MultiSelectorHeader";
import RouteEditorForm from "./components/RouteEditorForm";
import { RouteEditorFormValues } from "./components/RouteEditorForm";
import { ScheduleList } from "./components/ScheduleList";
import { useScheduleSelection } from "./hooks";
import {
    RouteEditorEditLocationState,
    RouteEditorModal,
    RouteEditorModalState,
    ScheduleMenuConfig,
} from "./RouteEditor.types";

const showDeleteBlockedNotification = () => {
    notification.warning({
        message: "Видалення заблоковане",
        description: "План на територію вже затверджено",
        duration: 4,
    });
};

interface RouteEditorEditProps {
    currentRoute: RouteDetails;
    routeId: string;
}

export const RouteEditorEdit: FC<RouteEditorEditProps> = ({
    currentRoute,
    routeId,
}) => {
    const navigate = useNavigate();
    const location = useLocation() as Location & {
        state: RouteEditorEditLocationState;
    };

    const dispatch = useAppDispatch();

    const { id: userId, role } = useAppSelector((s) => s.user);
    const { team, currentScheduleList } = useAppSelector((s) => s.sv);
    const [markets, setMarkets] = useState<Record<string, Market>>({});

    const [schedulesLoading, setSchedulesLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState<boolean>(false);
    const [modalState, setModalState] = useState<RouteEditorModalState>(null);
    const [isDeleteBlocked, setIsDeleteBlocked] = useState<boolean>(false);

    const isManager = isManagerRole(role);
    const canDelete = !isDeleteBlocked || !isManager; // менеджеры блокируются планом, не-менеджеры — нет

    const [routeEditorForm] = Form.useForm<RouteEditorFormValues>();

    const handleCloseModal = useCallback(() => setModalState(null), []);

    useEffect(() => {
        if (location?.state) {
            routeEditorForm.setFieldsValue(location?.state?.formValues);
            setIsDeleteBlocked(location?.state?.isDeleteBlocked);
        }
    }, [location.key, routeEditorForm]);

    const {
        filtersForm,
        watchedFilters,
        multiSelectMode,
        selectedSchedules,
        sortedAndFilteredSchedules,
        enterMultiSelectMode,
        exitMultiSelectMode,
        handleSelectSchedule,
        handleSelectAll,
        handleClearSelection,
    } = useScheduleSelection({ markets });

    const handleAddMarketClick = () => {
        navigate(buildRoutePath(ROUTES.RouteManagementAddMarket, { routeId }), {
            state: {
                formValues: routeEditorForm.getFieldsValue(),
                isDeleteBlocked,
            },
        });
    };

    const [handleSave, saveLoading] = useAsyncAction(async () => {
        const { routeAgent, routeName } = routeEditorForm.getFieldsValue();

        const agentId = isManager ? routeAgent : currentRoute.agentId;

        try {
            await patchRouteUpdate({
                name: routeName,
                id: routeId,
                ...(currentRoute.agentId !== agentId && {
                    newAgentId: agentId,
                }),
            });

            await patchSchedulerSetSchedulePoints({
                agentId,
                ownerId: currentRoute.ownerId,
                schedules: currentScheduleList,
            });

            if (isManager) {
                navigate(ROUTES.RouteManagement);
            } else {
                await dispatch(getUserMeThunk()).unwrap();
            }

            notification.success({
                message: "Маршрут збережено",
            });
        } catch (error) {
            const { displayMessage } = getApiError(error);
            notification.error({
                message: "Не вдалося зберегти маршрут",
                description: displayMessage,
            });
        }
    });

    const handleOpenDeleteModal = useCallback(() => {
        if (!canDelete) {
            showDeleteBlockedNotification();
            return;
        }

        const hasDebt = selectedSchedules.some(
            ({ marketId }) => markets[marketId]?.hasDebt,
        );

        if (hasDebt) {
            setModalState({
                type: RouteEditorModal.WarningDebt,
                payload: { isMultiSelect: selectedSchedules.length > 1 },
            });
        } else {
            setModalState({
                type: RouteEditorModal.DeleteSchedule,
                payload: {
                    marketIds: selectedSchedules.map(
                        ({ marketId }) => marketId,
                    ),
                    currentRouteId: currentRoute?.uuid,
                    isEmptyAfterDelete:
                        currentScheduleList.length === selectedSchedules.length,
                    createMode: false,
                },
            });
        }
    }, [
        canDelete,
        currentRoute?.uuid,
        currentScheduleList.length,
        markets,
        selectedSchedules,
    ]);

    // move — только для менеджеров
    const handleOpenMoveModal = useCallback(() => {
        setModalState({
            type: RouteEditorModal.MoveSchedule,
            payload: {
                marketIds: selectedSchedules.map(({ marketId }) => marketId),
                currentRouteId: currentRoute.uuid,
            },
        });
    }, [currentRoute.uuid, selectedSchedules]);

    const scheduleMenuConfig: ScheduleMenuConfig[] = [
        {
            type: "view",
            label: "Переглянути ТТ",
            onClick: (item: Schedule) => {
                navigate(
                    buildRoutePath(ROUTES.MarketDetails, {
                        marketId: item.marketId,
                    }),
                );
            },
        },
        {
            type: "edit",
            label: "Редагувати",
            onClick: (item) => {
                setModalState({
                    type: RouteEditorModal.EditSchedule,
                    payload: {
                        schedule: item,
                        createMode: false,
                        allSchedules: currentScheduleList,
                        route: currentRoute,
                    },
                });
            },
        },
        // move — только менеджерам
        ...(isManager
            ? [
                  {
                      type: "move" as const,
                      label: "Перемістити",
                      onClick: (item: Schedule) => {
                          setModalState({
                              type: RouteEditorModal.MoveSchedule,
                              payload: {
                                  marketIds: [item.marketId],
                                  currentRouteId: currentRoute.uuid,
                              },
                          });
                      },
                  },
              ]
            : []),
        {
            type: "delete",
            label: "Видалити",
            onClick: ({ marketId }) => {
                if (!canDelete) {
                    showDeleteBlockedNotification();
                    return;
                }

                if (markets[marketId]?.hasDebt) {
                    setModalState({
                        type: RouteEditorModal.WarningDebt,
                        payload: {
                            isMultiSelect: selectedSchedules.length > 1,
                        },
                    });
                    return;
                }

                setModalState({
                    type: RouteEditorModal.DeleteSchedule,
                    payload: {
                        marketIds: [marketId],
                        isEmptyAfterDelete: currentScheduleList.length === 1,
                        createMode: false,
                        currentRouteId: currentRoute.uuid,
                    },
                });
            },
        },
    ];

    const refreshSchedules = useCallback(async () => {
        try {
            setSchedulesLoading(true);
            const { result: schedules } = await dispatch(
                getSchedulerAllScheduleSvThunk({
                    agentId: currentRoute.agentId,
                }),
            ).unwrap();

            const { result: marketsData } = await getMarketInfoCollection({
                marketIds: schedules?.map((item) => item?.marketId),
            });

            const marketsMap = (marketsData ?? []).reduce<
                Record<string, Market>
            >((acc, market) => {
                acc[market.id] = market;
                return acc;
            }, {});

            setMarkets(marketsMap);
        } catch (error) {
            console.error(error);
        } finally {
            setSchedulesLoading(false);
        }
    }, [dispatch, routeId, location.key]);

    useEffect(() => {
        refreshSchedules();
    }, [location.key, refreshSchedules]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setInitialLoading(true);

                await getPlanConfigurationInfo({
                    mainRegionId: team?.mainRegionId,
                    partnerId: team?.partnerId,
                    targetDate: dayjs()
                        .startOf("month")
                        .format(backendDateFormat.date),
                })
                    .then(({ result }) => {
                        if (result?.meta?.approvedAt) setIsDeleteBlocked(true);
                    })
                    .catch(console.log);

                await dispatch(getSchedulerMyRoutesThunk({ ownerId: userId }));

                if (isManager) {
                    const teamFromResponse = await dispatch(
                        getUserTeamGetByManagerThunk({ managerId: userId }),
                    ).unwrap();

                    const isExist = teamFromResponse?.result?.members?.some(
                        (member) => member?.userId === currentRoute.agentId,
                    );

                    routeEditorForm.setFieldsValue({
                        routeName: currentRoute.name || "",
                        routeAgent: isExist ? currentRoute.agentId : null,
                    });
                } else {
                    routeEditorForm.setFieldsValue({
                        routeName: currentRoute.name || "",
                    });
                }
            } catch (err) {
                console.log(err);
            } finally {
                setInitialLoading(false);
            }
        };

        if (!location.state) {
            loadInitialData();
        }
    }, [dispatch, userId, routeEditorForm]);

    const handleAfterAction = useCallback(async () => {
        exitMultiSelectMode();
        await refreshSchedules();
    }, [exitMultiSelectMode, refreshSchedules]);

    return (
        <>
            {initialLoading && <Loading />}
            <Meta title="Редагувати маршрут" />

            {multiSelectMode ? (
                <MultiSelectorHeader
                    selectedCount={selectedSchedules.length}
                    allSelected={
                        sortedAndFilteredSchedules.length ===
                        selectedSchedules.length
                    }
                    onSelectAllItems={handleSelectAll}
                    onClearSelection={handleClearSelection}
                    onExitSelectionMode={exitMultiSelectMode}
                    onDeleteSelected={handleOpenDeleteModal}
                    onMoveSelected={isManager ? handleOpenMoveModal : undefined}
                />
            ) : (
                <Header
                    title="Редагувати маршрут"
                    path={isManager && ROUTES.RouteManagement}
                />
            )}

            <MainContainer $paddingTop="0" $paddingBottom="8px">
                <div
                    style={{
                        overflowY: "auto",
                        paddingTop: 16,
                        height: "100%",
                    }}
                >
                    {!multiSelectMode && (
                        // форма с полем агента — только менеджерам
                        <RouteEditorForm
                            form={routeEditorForm}
                            showAgentField={isManager}
                        />
                    )}

                    <FiltersPanel form={filtersForm} />

                    {schedulesLoading ? (
                        <BlockLoader />
                    ) : sortedAndFilteredSchedules?.length ? (
                        <ScheduleList
                            displaySchedules={sortedAndFilteredSchedules}
                            multiSelectMode={multiSelectMode}
                            selectedSchedules={selectedSchedules}
                            handleSelectSchedule={handleSelectSchedule}
                            handleTap={enterMultiSelectMode}
                            dayFilter={watchedFilters?.day}
                            scheduleMenuConfig={scheduleMenuConfig}
                            markets={markets}
                        />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Немає активних точок"
                        />
                    )}
                </div>

                <Flex
                    vertical
                    gap={8}
                    style={{ paddingTop: 8, marginTop: "auto" }}
                >
                    <BaseButton
                        type="default"
                        onClick={handleAddMarketClick}
                        disabled={saveLoading}
                    >
                        Додати ТТ
                    </BaseButton>
                    <BaseButton
                        type="primary"
                        onClick={handleSave}
                        loading={saveLoading}
                    >
                        Зберегти
                    </BaseButton>
                </Flex>
            </MainContainer>

            <ModalsContainer
                onClose={handleCloseModal}
                modalState={modalState}
                onAfterConfirm={handleAfterAction}
            />
        </>
    );
};
